import { DropdownButtonType, List,ListItem, RadioButtonWithLabelGroupWidget, RadioButtonWithLabelOption, SingleSelectDropdown } from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import {FeatureFlags} from '~commonInfra/featureFlags/FeatureFlags';

import { sanitize } from '../../../../src/commonInfra/utils/Sanitize';
import { siteUrlsSelector } from '../../../bootstrap/redux/selectors/SiteUrlsSelectors';
import { getAppStore } from '../../../bootstrap/redux/store/AppStore';
import { Messages } from '../../../codegen/Localize';
import { Sites } from '../../../siteManagement/services/resources/Sites';
import { AuthenticationProviderSelectorProperties } from '../model/AuthenticationProviderSelectorProperties';
import {
    AuthenticationProviderAlias,
    AuthenticationProviderType,
    DefaultAuthenticationProvider,
    IAuthenticationProvider,
    ISiteAuthenticationSettings,
    SiteAuthenticationSettingsService,
    TabIdWithMFAAuthenticationProvider,
} from '../services/SiteAuthenticationSettingsService';
import {
    authenticationProviderOptionDescriptionStyle,
    authenticationProviderOptionListItem,    authenticationProviderOptionStyle,
    authenticationProviderOptionTitleStyle,
    authenticationRadioButtonsStyle,
    configureAuthenticationLinkStyle} from '../styles/AuthenticationProviderSelectorStyle';


export const AUTH_LABEL_TEST_ID = 'authentication-label';

export type AuthenticationProviderSelectorProps = AuthenticationProviderSelectorProperties & {
    onProviderChange: (provider: IAuthenticationProvider) => void;
    onProviderIdsChange: (ids: string[]) => void;
    onAuthSettingChange: (authSetting: string) => void;
    displayUsername?: string;
}

export type AuthenticationProviderSelectorState = {
    defaultProvider: IAuthenticationProvider;
    tabIdMfaProvider: IAuthenticationProvider;
    enabledAdditionalProviders: IAuthenticationProvider[];
    authenticationUrl: string;
    getProviderDescription: (provider: IAuthenticationProvider, inviteNotificationEnabledForSite: boolean) => string;
    getProviderDisplayName: (provider: IAuthenticationProvider) => string;
    inviteNotificationEnabledForSite?: boolean;
    providerButtons: RadioButtonWithLabelOption<string>[];
    providerList: ListItem[];
    radioButtonIndex: number;
}

export class AuthenticationProviderSelector extends React.Component<AuthenticationProviderSelectorProps, AuthenticationProviderSelectorState> {
    private defaultDisplayName = (provider: IAuthenticationProvider) => SiteAuthenticationSettingsService.getProviderDisplayName(provider);
    private defaultDescription = (provider: IAuthenticationProvider) => '';

    constructor(props: AuthenticationProviderSelectorProps) {
        super(props);
        this.state = {
            defaultProvider: DefaultAuthenticationProvider,
            tabIdMfaProvider: TabIdWithMFAAuthenticationProvider,
            enabledAdditionalProviders: [],
            authenticationUrl: '',
            getProviderDescription: this.props.getProviderDescription || this.defaultDescription,
            getProviderDisplayName: this.props.getProviderDisplayName || this.defaultDisplayName,
            providerButtons: [],
            radioButtonIndex: 0,
            providerList: []
        };
    }

    public componentDidMount() {
        this.setState({authenticationUrl: siteUrlsSelector(getAppStore().getState()).settingsAuthenticationUrl()});
        SiteAuthenticationSettingsService.getSiteAuthenticationSettings()
            .then((settings: ISiteAuthenticationSettings) => {
                this.setState({defaultProvider: settings.defaultProvider});
                this.setState({tabIdMfaProvider: settings.tabIdMfaProvider});
                this.setState({enabledAdditionalProviders: this.getAdditionalProviders(settings)});
                this.setProviderIds(settings);

                if (this.state.enabledAdditionalProviders.length === 0) {
                    this.setProvider(settings, this.state.defaultProvider.id);
                }
                this.setState({providerList: this.getAuthenticationTypesList()});

                Sites.instance.getCurrentSiteSettingsForSiteAdmin()
                    .then((data: any) => {
                        this.setState({inviteNotificationEnabledForSite: data.siteAdminSettings.siteInviteEmailEnabled});
                        this.setState({providerButtons: this.getProviderButtons()});
                        const radioButtonIndex = this.props.authenticationProvider
                            ? this.state.providerButtons.findIndex(
                                button => button.value === this.props.authenticationProvider.id)
                            : undefined;
                        this.setState({radioButtonIndex: radioButtonIndex});
                    });
            });
    }

    private setProvider(settings: ISiteAuthenticationSettings, id: string) {
        const authProvider = FeatureFlags.featureFlagForMultipleIDPSupportEnabled ?
                SiteAuthenticationSettingsService.findProviderForMultipleIdp(settings, id, id) : SiteAuthenticationSettingsService.findProvider(settings, id);
        const authSetting = SiteAuthenticationSettingsService.getAuthSetting(id, settings);
        this.props.onProviderChange(authProvider);
        this.props.onAuthSettingChange(authSetting);
    }

    private setProviderWithOnlyId(id: string, checkedIndex: number) {
        SiteAuthenticationSettingsService.getSiteAuthenticationSettings()
            .then((settings: ISiteAuthenticationSettings) => {
                this.setProvider(settings, id);
                this.setState({radioButtonIndex: this.state.providerButtons.findIndex(button => button.value === id)});
            });
    }

    private setProviderIds(settings: ISiteAuthenticationSettings) {
        const ids = this.props.authenticationProviderIds || [settings.defaultProvider.id];
        this.props.onProviderIdsChange(ids);
    }

    private getAdditionalProviders(settings: ISiteAuthenticationSettings): IAuthenticationProvider[] {
        let additionalProviders: IAuthenticationProvider[] = [];
        if (FeatureFlags.featureFlagForMultipleIDPSupportEnabled) {
          additionalProviders =  _.filter([settings.tabIdMfaProvider].concat(settings.allProviders), (provider: IAuthenticationProvider) => provider.isEnabled);
        } else {
          additionalProviders =  _.filter([settings.tabIdMfaProvider].concat(settings.externalProviders), (provider: IAuthenticationProvider) => provider.isEnabled);
        }
        return additionalProviders;
    }

    private getProviderButtons(): RadioButtonWithLabelOption<string>[] {
        let providerButtons: RadioButtonWithLabelOption<string>[] = [];
        if (this.state.defaultProvider.isEnabled) {
            providerButtons.push(
                {
                    label: this.state.getProviderDisplayName(this.state.defaultProvider),
                    detail: <div style={authenticationProviderOptionDescriptionStyle}
                        dangerouslySetInnerHTML={{ __html: sanitize(this.state.getProviderDescription(this.state.defaultProvider, this.state.inviteNotificationEnabledForSite)) }}
                    />,
                    testId: 'default-auth-provider-radio-button',
                    value: DefaultAuthenticationProvider.id
                }
            );
        }

        this.state.enabledAdditionalProviders.forEach((provider) => {
            const additionalProviderButtonDetail =
                <div style={authenticationProviderOptionDescriptionStyle}
                    dangerouslySetInnerHTML={{ __html: sanitize(this.state.getProviderDescription(provider, this.state.inviteNotificationEnabledForSite)) }}
                />;

            //TabId + MFA should be first, if enabled
            if (provider.id === this.state.tabIdMfaProvider.id) {
                providerButtons.unshift(
                    {
                        label: this.state.getProviderDisplayName(provider),
                        detail: additionalProviderButtonDetail,
                        testId: 'tolmfa-auth-provider-radio-button',
                        value: provider.id
                    }
                );
            }
            else {
                providerButtons.push(
                    {
                        label: FeatureFlags.featureFlagForMultipleIDPSupportEnabled? provider.idpConfigurationName : this.state.getProviderDisplayName(provider),
                        detail: additionalProviderButtonDetail,
                        testId: 'external-auth-provider-radio-button',
                        value: provider.id
                    }
                );
            }
        });

        return providerButtons;
    }

    private getAuthenticationTypesList(): ListItem[] {
        const authenticationItems: ListItem[] = [];

        if (this.state.defaultProvider.isEnabled) {
            authenticationItems.push(
                {
                    content:
                    <div style={authenticationProviderOptionListItem}>
                        <div style={authenticationProviderOptionTitleStyle}>{this.state.getProviderDisplayName(this.state.defaultProvider)}</div>
                        <div style={authenticationProviderOptionDescriptionStyle}
                            dangerouslySetInnerHTML={{ __html: sanitize(this.state.getProviderDescription(this.state.defaultProvider, this.state.inviteNotificationEnabledForSite)) }}
                        />
                    </div>,
                    testId: 'default-auth-provider-radio-button',
                    value: DefaultAuthenticationProvider.id
                }
            );
        }

        this.state.enabledAdditionalProviders.forEach((provider) => {
            if (provider.id === this.state.tabIdMfaProvider.id) {
                authenticationItems.unshift(
                    {
                        content:
                        <div style={authenticationProviderOptionListItem}>
                            <div style={authenticationProviderOptionTitleStyle}>{this.state.getProviderDisplayName(provider)}</div>
                            <div style={authenticationProviderOptionDescriptionStyle}
                            dangerouslySetInnerHTML={{ __html: sanitize(this.state.getProviderDescription(provider, this.state.inviteNotificationEnabledForSite)) }}
                            />
                        </div>,
                        testId: 'tolmfa-auth-provider-radio-button',
                        value: provider.id
                    }
                );
            } else if (provider.isEnabled) {
                authenticationItems.push(
                    {
                        content:
                        <div style={authenticationProviderOptionListItem}>
                            <div style={authenticationProviderOptionTitleStyle}>{provider.idpConfigurationName}</div>
                            <div style={authenticationProviderOptionDescriptionStyle}
                                dangerouslySetInnerHTML={{ __html: sanitize(this.getExternalProviderDescription(provider)) }}
                            />
                        </div>,
                        testId: 'configuration-embedded-button',
                        value: provider.id
                    }
                );
            }
        });

        return authenticationItems;
    }

    private getExternalProviderDescription(provider: IAuthenticationProvider): string {
        if (provider?.alias === AuthenticationProviderAlias.google) {
            return Messages.addUsersWithAuthentication_description_Google();
        }
        if (provider?.alias === AuthenticationProviderAlias.salesforce) {
            return Messages.addUsersWithAuthentication_description_Salesforce();
        }
        if (provider?.type === AuthenticationProviderType.saml) {
            return Messages.addUsersWithAuthentication_description_SAML();
        }
        if (provider?.alias === AuthenticationProviderAlias.openIdConnect) {
            return Messages.addUsersWithAuthentication_description_OIDC();
        }
        if (!provider.alias && (provider.type === AuthenticationProviderType.oidc)) {
            return Messages.addUsersWithAuthentication_description_OIDC();
        }
        return '';
      }

    private getDropdownDisplayValue(): string {
        if (!this.props.authenticationProvider) {
            return Messages.userAuthSetting_select_authentication_method();
        } else if (this.props.authenticationProvider?.id === DefaultAuthenticationProvider.id) {
            return this.state.getProviderDisplayName(this.props.authenticationProvider);
        } else if (this.props.authenticationProvider?.id === this.state.tabIdMfaProvider.id) {
            return this.state.getProviderDisplayName(this.props.authenticationProvider);
        } else if (this.props.authenticationProvider) {
            return this.props.authenticationProvider.idpConfigurationName;
        }
    }

    private renderAdditionalProviders(): JSX.Element {
        switch(this.state.enabledAdditionalProviders.length) {
            case 0:
                return (
                <div>
                    <div style={authenticationProviderOptionStyle} data-tb-test-id={AUTH_LABEL_TEST_ID}>
                        {this.state.getProviderDisplayName(this.state.defaultProvider)}
                        &nbsp;
                    </div>
                    <div style={authenticationProviderOptionDescriptionStyle} dangerouslySetInnerHTML={{__html:
                        sanitize(this.state.getProviderDescription(this.state.defaultProvider, this.state.inviteNotificationEnabledForSite))}}/>
                    <div style={configureAuthenticationLinkStyle}>
                        <a href={this.state.authenticationUrl}>
                            {Messages.configureAdditionalAuthenticationMethods()}
                        </a>
                    </div>
                </div>
                );
            default:
                return (
                    <>
                        {FeatureFlags.featureFlagForMultipleIDPSupportEnabled ?
                            <div style={{marginBottom: '8px'}}>
                                <SingleSelectDropdown
                                        list={<List items={this.state.providerList} style={{}}/>}
                                        // i'll probably need to change the setProvider method for tabid and tabid w mfa.  setProvider()?
                                        onChange={this.setProviderWithOnlyId.bind(this)}
                                        value={this.props.authenticationProvider?.id}
                                        label={<div>
                                                    {this.props.displayUsername ?
                                                        <div>{Messages.setUserAuthentication_description_username({USER_NAME: this.props.displayUsername})}</div>
                                                    : <div>{Messages.setUserAuthentication_selected_users_description()}</div>
                                                    }
                                                    <div>{Messages.authentication()}</div>
                                               </div>}
                                        type={DropdownButtonType.Outline}
                                        style={{}}
                                        testId='add-users-authentication-dropdown'>
                                    {this.getDropdownDisplayValue()}
                                </SingleSelectDropdown>
                            </div>
                        :
                            <div style={authenticationRadioButtonsStyle}>
                                <RadioButtonWithLabelGroupWidget
                                aria-label={Messages.authentication()}
                                buttonList={this.state.providerButtons}
                                checkedIndex={this.state.radioButtonIndex}
                                handleChange={this.setProviderWithOnlyId.bind(this)}
                                style={{maxHeight: '200px', overflowY: 'auto'}}/>
                            </div>
                        }
                    </>
                );
        }
    }

    public render(): JSX.Element {
        return (
            <div>
                {this.renderAdditionalProviders()}
            </div>
        );
    }
}
